import React, { useState, useRef, useMemo, useEffect } from "react";
import { Overlay, Popover, FormControl } from "react-bootstrap";
import { fetchAllLiveOrgs } from "../api/usersApi";
import "./ClientFilterPopover.css";

/**
 * ClientFilterPopover
 * Lets the user pick a client to view the feature timeline.
 * Uses ONLY live client data from the API (fetchAllLiveOrgs), not localStorage.
 * @param {function} onApply - called with { org, center, clientId }
 */
const ClientFilterPopover = ({ onApply }) => {
    const [show, setShow] = useState(false);
    const [search, setSearch] = useState("");
    const [liveClients, setLiveClients] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState("");
    const targetRef = useRef(null);

    // Fetch live clients from API when popover is opened (no localStorage)
    useEffect(() => {
        if (!show) return;
        setLoadError("");
        setLoading(true);
        fetchAllLiveOrgs()
            .then((clients) => {
                setLiveClients(clients || []);
            })
            .catch((err) => {
                console.error("[ClientFilterPopover] Failed to load live clients:", err);
                setLoadError(err?.message || "Could not load clients. Try again.");
                setLiveClients([]);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [show]);

    const searchLower = search.toLowerCase().trim();
    const filteredList = useMemo(
        () =>
            liveClients.filter(
                (c) =>
                    (c.customerName || "").toLowerCase().includes(searchLower) ||
                    (c.displayName || "").toLowerCase().includes(searchLower) ||
                    (c.clientId || "").toLowerCase().includes(searchLower)
            ),
        [liveClients, searchLower]
    );

    const handleOpen = () => {
        setShow(true);
        setSearch("");
    };

    const handleSelectClient = (client) => {
        const { org, center, clientId } = client;
        if (typeof onApply === "function") {
            onApply({ org: org || "", center: center || "", clientId: clientId || "" });
        }
        setShow(false);
        setSearch("");
    };

    return (
        <>
            <button
                ref={targetRef}
                type="button"
                className="release-tracking-page__filter-btn release-tracking-page__filter-btn--small"
                onClick={() => (show ? setShow(false) : handleOpen())}
                aria-label="Filter by client"
                aria-expanded={show}
                title="Filter by client"
            >
                <span className="release-tracking-page__filter-icon" aria-hidden="true">
                    <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                    </svg>
                </span>
                <span className="release-tracking-page__filter-btn-text">Filter by Client</span>
            </button>

            <Overlay
                show={show}
                target={targetRef}
                placement="bottom-end"
                rootClose
                onHide={() => setShow(false)}
            >
                <Popover
                    className="client-filter-popover client-filter-popover--center-only"
                    id="client-filter-popover"
                >
                    <Popover.Body className="client-filter-popover__body">
                        <label className="client-filter-popover__label" htmlFor="client-search-input">Select Client</label>
                        <FormControl
                            id="client-search-input"
                            ref={(el) => {
                                // Focus without scrolling — autoFocus causes
                                // the browser to scroll the page to this input.
                                if (el && show) {
                                    requestAnimationFrame(() => {
                                        el.focus({ preventScroll: true });
                                    });
                                }
                            }}
                            type="text"
                            placeholder="Search client name..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="client-filter-popover__search"
                            size="sm"
                        />
                        <ul className="client-filter-popover__list">
                            {loading ? (
                                <li className="client-filter-popover__empty">
                                    Loading clients…
                                </li>
                            ) : loadError ? (
                                <li className="client-filter-popover__empty">
                                    {loadError}
                                </li>
                            ) : liveClients.length === 0 ? (
                                <li className="client-filter-popover__empty">
                                    No clients found.
                                </li>
                            ) : filteredList.length === 0 ? (
                                <li className="client-filter-popover__empty">
                                    No match found.
                                </li>
                            ) : (
                                filteredList.map((client, idx) => (
                                    <li key={`${client.clientId || "client"}-${idx}`}>
                                        <button
                                            type="button"
                                            className="client-filter-popover__item"
                                            onClick={() => handleSelectClient(client)}
                                        >
                                            {client.customerName || client.displayName || client.clientId}
                                        </button>
                                    </li>
                                ))
                            )}
                        </ul>
                    </Popover.Body>
                </Popover>
            </Overlay>
        </>
    );
};

export default ClientFilterPopover;
